import { useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IDepartmentResponse } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getDepartmentUUID = createUUID();

export const useDepartment = () => {
  const stateManager = useComponentState();

  const [department, setDepartment] = useState<IDepartmentResponse | null>(
    null
  );
  const [refresh, setRefresh] = useState<boolean>(false);

  const getDepartment = (id: string) => {
    stateManager.abort(getDepartmentUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.departments.get(id);

      if (response.status === HTTP_STATUSES.OK) {
        setDepartment(response.data);
      }

      return response;
    });
  };

  const updateDepartment = (id: string, data: Partial<IDepartmentResponse>) => {
    stateManager.run(async () => {
      const response = await subscriptionService.departments.update({
        id,
        data: {
          ...department,
          ...data,
        },
        toastMessageConfig: {
          success: {
            text: `${data.name} updated successfully.`,
          },
          failure: {
            text: `Failed to update ${data.name}. Please try again.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setDepartment(response.data);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const deleteDepartment = (department: IDepartmentResponse) => {
    stateManager.run(async () => {
      const response = await subscriptionService.departments.delete({
        data: department,
        toastMessageConfig: {
          success: {
            text: `${department.name} deleted successfully.`,
          },
          failure: {
            text: `Failed to delete ${department.name}. Please try again.`,
          },
        },
      });

      if (response.status === HTTP_STATUSES.OK) {
        setDepartment(null);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  const createDepartment = (data: Partial<IDepartmentResponse>) => {
    return stateManager.run(async () => {
      const response = await subscriptionService.departments.create({
        data,
        toastMessageConfig: {
          success: {
            text: `${data.name} created successfully.`,
          },
          failure: {
            text: `Failed to create ${data.name}. Please try again.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setDepartment(response.data);
        setRefresh((prev) => !prev);
      }

      return response;
    });
  };

  return {
    department,
    loading: stateManager.isBusy(),
    getDepartment,
    updateDepartment,
    deleteDepartment,
    createDepartment,
    refresh,
    setRefresh,
  };
};
